// pages/BulkRuleCreate.jsx
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import { ArrowLeft, Upload } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useToast } from "components/Toast";

export default function BulkRuleCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const companyId = useEffectiveCompanyId();

  const [csvFile, setCsvFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("standard");

  function handleDownloadTemplate() {
    const header =
      "bin,category,campaign,country,implementedDate,mid1,allocation1,mid2,allocation2,mid3,allocation3\n";
    const sample =
      "414908,Massager,,US,2025-10-01,EMSENSE CHECKOUT.COM EUR,100,,,,,,\n" +
      "414709,Detox Tea,Lulutox,US,2025-10-05,MID123,70,MID456,30,,,,\n";
    const blob = new Blob([header + sample], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "bulk-rules-template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const rows = text
        .split("\n")
        .map((r) => r.trim())
        .filter(Boolean)
        .map((row) => row.split(",").map((c) => c.trim()));
      setParsedRows(rows);
    };
    reader.readAsText(file);
  }

  // ✅ Compute validation errors
  const rowErrors = useMemo(() => {
    if (parsedRows.length <= 1) return [];
    return parsedRows.slice(1).map((row) => {
      const mids = [];
      for (let i = 5; i < row.length; i += 2) {
        const mid = row[i];
        const allocation = row[i + 1];
        if (mid && allocation) {
          mids.push({ mid, allocation: Number(allocation) });
        }
      }
      const totalAllocation = mids.reduce(
        (sum, m) => sum + (isNaN(m.allocation) ? 0 : m.allocation),
        0
      );
      const hasError = mids.length === 0 || totalAllocation !== 100;
      return hasError;
    });
  }, [parsedRows]);

  const hasErrors = rowErrors.some((e) => e);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!csvFile || parsedRows.length <= 1) {
      toast({
        status: "warning",
        title: "No Data",
        description: "Please upload a valid CSV file.",
      });
      return;
    }
    if (hasErrors) {
      toast({
        status: "warning",
        title: "Validation Failed",
        description: "Fix errors in the table before continuing.",
      });
      return;
    }

    setSaving(true);
    try {
      // skip header
      const rules = parsedRows.slice(1).map((row) => {
        const [bin, category, campaign, country, implementedDate, ...rest] =
          row;

        // routing mids/allocations
        const routing = [];
        for (let i = 0; i < rest.length; i += 2) {
          const mid = rest[i];
          const allocation = rest[i + 1];
          if (mid && allocation) {
            routing.push({ mid, allocation: Number(allocation) });
          }
        }

        const rule = {
          scope: { type: "BIN", value: bin },
          routing,
          status: "Live",
          constraints: [],
        };

        // ✅ add constraints
        if (category) {
          rule.constraints.push({ type: "CATEGORY", value: category });
        }
        if (campaign && campaign !== "ALL") {
          rule.constraints.push({ type: "CAMPAIGN", value: campaign });
        }
        if (country) {
          rule.constraints.push({ type: "COUNTRY", value: country });
        }

        // ✅ override implementedDate
        if (implementedDate) {
          const d = new Date(implementedDate);
          rule.createdAt = d;
          rule.updatedAt = d;
        }

        return rule;
      });

      // ✅ include mode flag in payload
      const res = await makeApiRequest(
        "post",
        apiRoutes.bulkCreateRules,
        { id: user.id, accessToken: user.accessToken },
        companyId,
        { rules, mode }
      );

      toast({
        status: "success",
        title:
          mode === "versioned"
            ? "Version history upload complete"
            : "Bulk upload complete",
        description: `${res.data.created} rules ${
          mode === "versioned" ? "uploaded" : "created"
        }, ${res.data.skipped.length} skipped.`,
        action: {
          label: "View Rules",
          onClick: () => navigate("/rules"),
        },
      });

      navigate("/rules");
    } catch (err) {
      console.error("[BulkRuleCreate] Failed to upload rules", err);
      toast({
        status: "destructive",
        title: "Upload failed",
        description:
          "There was a problem creating your rules. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Bulk Create Rules"
        subtitle="Upload a CSV with BINs, categories, campaigns, countries, and routing configuration. You can optionally upload historical versions of rules."
        actions={
          <button className="btn btn-ghost" onClick={() => navigate("/rules")}>
            <ArrowLeft className="h-4 w-4" />
            Cancel
          </button>
        }
      />

      <div className="container py-6 space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ✅ Mode Selection */}
          <section className="surface rounded-xl p-6 ring-1 ring-border space-y-4">
            <h2 className="text-lg font-semibold">Upload Mode</h2>
            <p className="text-sm text-muted-foreground">
              Choose whether to create new rules or upload version history for
              existing ones.
            </p>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="mode"
                  value="standard"
                  checked={mode === "standard"}
                  onChange={(e) => setMode(e.target.value)}
                />
                <span>Create New Rules (default)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="mode"
                  value="versioned"
                  checked={mode === "versioned"}
                  onChange={(e) => setMode(e.target.value)}
                />
                <span>Upload Version History</span>
              </label>
            </div>
          </section>

          {/* CSV Upload */}
          <section className="surface rounded-xl p-6 ring-1 ring-border space-y-4">
            <h2 className="text-lg font-semibold">Upload CSV</h2>
            <p className="text-sm text-muted-foreground">
              The CSV should include BIN, Category (optional), Campaign
              (optional), Country (optional), Implemented Date (optional), and
              routing configuration.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDownloadTemplate}
              >
                Download Template
              </button>
              <label className="btn btn-secondary inline-flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Choose File
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            </div>
            {csvFile && (
              <div className="mt-2 text-sm text-muted-foreground">
                Selected file: <strong>{csvFile.name}</strong>
              </div>
            )}
          </section>

          {/* Preview table */}
          {parsedRows.length > 1 && (
            <section className="surface rounded-xl p-6 ring-1 ring-border space-y-4">
              <h2 className="text-lg font-semibold">Preview Rules</h2>

              {hasErrors && (
                <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm font-medium">
                  {rowErrors.filter(Boolean).length} of {rowErrors.length} rows
                  are invalid. Fix errors before continuing.
                </div>
              )}
              {!hasErrors && (
                <div className="p-3 rounded-md bg-green-50 text-green-700 text-sm font-medium">
                  All {rowErrors.length} rows look valid. 🎉
                </div>
              )}

              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-border">
                    <th className="pb-2">BIN</th>
                    <th className="pb-2">Category</th>
                    <th className="pb-2">Campaign</th>
                    <th className="pb-2">Country</th>
                    <th className="pb-2">Implemented Date</th>
                    <th className="pb-2">MIDs / Allocations</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {parsedRows.slice(1).map((row, idx) => {
                    const [
                      bin,
                      category,
                      campaign,
                      country,
                      implementedDate,
                      ...rest
                    ] = row;

                    const mids = [];
                    for (let i = 0; i < rest.length; i += 2) {
                      const mid = rest[i];
                      const allocation = rest[i + 1];
                      if (mid && allocation) {
                        mids.push({ mid, allocation: Number(allocation) });
                      }
                    }

                    const totalAllocation = mids.reduce(
                      (sum, m) =>
                        sum + (isNaN(m.allocation) ? 0 : m.allocation),
                      0
                    );
                    const hasError =
                      mids.length === 0 || totalAllocation !== 100;

                    return (
                      <tr key={idx}>
                        <td className="py-2 font-mono">{bin}</td>
                        <td className="py-2">{category || "—"}</td>
                        <td className="py-2">{campaign || "ALL"}</td>
                        <td className="py-2">{country || "—"}</td>
                        <td className="py-2">{implementedDate || "—"}</td>
                        <td className="py-2">
                          {mids.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {mids.map((m, i) => (
                                <span
                                  key={i}
                                  className="inline-block bg-muted px-2 py-1 rounded-full text-xs"
                                >
                                  {m.mid} ({m.allocation}%)
                                </span>
                              ))}
                              {hasError && (
                                <span className="inline-block bg-red-50 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
                                  ⚠ {totalAllocation}% (must equal 100%)
                                </span>
                              )}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          )}

          {/* Submit */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!csvFile || hasErrors || saving}
            >
              {saving
                ? "Saving..."
                : mode === "versioned"
                ? "Upload Version History"
                : "Validate & Continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
